import React, { useContext, useEffect, useState } from "react";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";
import "./workspaceInviteModal.css";

export default function WorkspaceInviteModal() {
    const { auth, refreshStatus } = useContext(AuthContext);
    const [invites, setInvites] = useState([]);
    const [dismissedIds, setDismissedIds] = useState([]);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        const userId = auth?.user?.user_id;
        if (!userId) {
            setInvites([]);
            return;
        }
        const fetchInvites = () => {
            if (document.hidden) return;
            API.getMyPendingInvites(userId)
                .then((res) => setInvites(res.data))
                .catch(() => {});
        };
        fetchInvites();
        const interval = setInterval(fetchInvites, 30 * 1000);
        const handleVisibility = () => { if (!document.hidden) fetchInvites(); };
        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [auth?.user?.user_id]);

    const handleAccept = (invite) => {
        setBusyId(invite.member_id);
        API.acceptTeamInvite(invite.team_id, invite.member_id, auth.user.user_id)
            .then(() => {
                setInvites((prev) => prev.filter((i) => i.member_id !== invite.member_id));
                refreshStatus();
            })
            .catch((err) => alert(err.response?.data?.message || "Error accepting invite."))
            .finally(() => setBusyId(null));
    };

    const handleDecline = (invite) => {
        setBusyId(invite.member_id);
        API.declineTeamInvite(invite.team_id, invite.member_id, auth.user.user_id)
            .then(() => {
                setInvites((prev) => prev.filter((i) => i.member_id !== invite.member_id));
            })
            .catch((err) => alert(err.response?.data?.message || "Error declining invite."))
            .finally(() => setBusyId(null));
    };

    const visibleInvites = invites.filter((i) => !dismissedIds.includes(i.member_id));

    if (visibleInvites.length === 0) return null;

    const initials = (invite) =>
        (invite.TeamAccount?.team_acronym || invite.TeamAccount?.team_name || "?").slice(0, 2).toUpperCase();

    const remindLater = () => setDismissedIds((prev) => [...prev, ...visibleInvites.map((i) => i.member_id)]);

    return (
        <div className="modal fade show d-block wim-overlay" tabIndex="-1" onClick={remindLater}>
            <div className="modal-dialog modal-dialog-centered wim-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content wim-content">
                    <div className="wim-hero">
                        <div className="wim-hero-glow" />
                        <i className="bi bi-briefcase-fill wim-hero-icon" />
                        <h5 className="wim-hero-title">You're Invited!</h5>
                        <p className="wim-hero-sub">
                            {visibleInvites.length > 1
                                ? `You have ${visibleInvites.length} pending Workspace invites`
                                : "Join your team's Workspace on 'elloGovna"}
                        </p>
                    </div>
                    <div className="modal-body wim-body">
                        {visibleInvites.map((invite) => (
                            <div key={invite.member_id} className="wim-invite-row">
                                {invite.TeamAccount?.Organization?.logo ? (
                                    <img
                                        src={invite.TeamAccount.Organization.logo}
                                        alt=""
                                        className="wim-team-logo"
                                    />
                                ) : (
                                    <div
                                        className="wim-team-badge"
                                        style={invite.TeamAccount?.tag_color ? { background: invite.TeamAccount.tag_color } : undefined}
                                    >
                                        {initials(invite)}
                                    </div>
                                )}
                                <div className="wim-invite-text">
                                    <strong>{invite.TeamAccount?.team_name}</strong>
                                    <span className="wim-invite-sub">wants you on their team</span>
                                </div>
                                <div className="wim-invite-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm wim-btn-accept"
                                        disabled={busyId === invite.member_id}
                                        onClick={() => handleAccept(invite)}
                                    >
                                        <i className="bi bi-check-lg me-1" />Accept
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link wim-btn-decline"
                                        disabled={busyId === invite.member_id}
                                        onClick={() => handleDecline(invite)}
                                    >
                                        Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="modal-footer wim-footer">
                        <button type="button" className="btn btn-sm btn-link wim-later-btn" onClick={remindLater}>
                            Remind me later
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
